import { API_BASE } from '../apiBase';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import {
  Settings2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Inbox,
  Trash2,
  Upload,
} from 'lucide-react';
import GlobalReceiptScanner from '../GlobalReceiptScanner';

const ExpenseLedger = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // 1. DYNAMIC DATABASE DATA
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  // Row to briefly highlight when arriving from a project's expense row.
  const [highlightId, setHighlightId] = useState(null);
  // Receipt-number key of the purchase to briefly highlight when a split-return
  // badge is clicked (the original purchase that split refunds).
  const [highlightPurchaseKey, setHighlightPurchaseKey] = useState(null);

  // ---- Unallocated (orphan) queue + drag-drop upload ----
  const [unallocated, setUnallocated] = useState([]);
  const [showOrphans, setShowOrphans] = useState(false);
  const [ledgerDragging, setLedgerDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [allocPO, setAllocPO] = useState({});         // itemId -> typed PO
  const [allocCat, setAllocCat] = useState({});       // itemId -> chosen cost category id
  const [allocType, setAllocType] = useState({});     // itemId -> expense TYPE override ('' = auto)
  const [costCategories, setCostCategories] = useState([]);
  // Double-click-a-row-to-edit-PO modal.  { id, store, current, draft }
  const [poEdit, setPoEdit] = useState(null);
  const [savingPo, setSavingPo] = useState(false);
  const [poEditError, setPoEditError] = useState('');
  // Company-wide "Awaiting PM review" oversight (Super Admin only).
  const isSuperadmin = (localStorage.getItem('role') || 'employee') === 'superadmin';
  const canScan = ['superadmin', 'admin'].includes(localStorage.getItem('role') || 'employee');
  const [pendingReviews, setPendingReviews] = useState([]);
  const [nudgedId, setNudgedId] = useState(null);
  const orphanRef = useRef(null);
  const orphanBtnRef = useRef(null);
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchUnallocated = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/expenses/unallocated`, { headers: authHeader() });
      if (res.ok) { const d = await res.json(); setUnallocated(d.items || []); }
    } catch (_) {}
  }, []);

  const fetchPendingReviews = useCallback(async () => {
    if (!isSuperadmin) return;
    try {
      const res = await fetch(`${API_BASE}/pending-reviews/overview`, { headers: authHeader() });
      if (res.ok) { const d = await res.json(); setPendingReviews(d.rows || []); }
    } catch (_) {}
  }, [isSuperadmin]);

  useEffect(() => {
    fetchUnallocated();
    fetchPendingReviews();
    const id = setInterval(() => { fetchUnallocated(); fetchPendingReviews(); }, 10000);
    return () => clearInterval(id);
  }, [fetchUnallocated, fetchPendingReviews]);

  const nudgePM = async (row) => {
    try {
      const res = await fetch(`${API_BASE}/pending-reviews/nudge`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: row.projectId }),
      });
      if (res.ok) { setNudgedId(row.projectId); setTimeout(() => setNudgedId(null), 2500); }
      else { const e = await res.json().catch(() => ({})); alert(e.error || 'Could not nudge.'); }
    } catch (_) { alert('Could not nudge.'); }
  };

  const pendingReviewTotal = pendingReviews.reduce((s, r) => s + (r.count || 0), 0);

  // Cost categories (overhead buckets) for filtering + allocation.
  useEffect(() => {
    fetch(`${API_BASE}/cost-categories`, { headers: authHeader() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setCostCategories(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // Refetch the booked ledger (used after allocating an item).
  const refetchLedger = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/expenses`, { headers: authHeader() });
      if (res.ok) setData(await res.json());
    } catch (_) {}
  }, []);

  // Save a new PO from the double-click editor. Empty = move to Unallocated.
  const savePoEdit = async () => {
    if (!poEdit) return;
    setSavingPo(true);
    setPoEditError('');
    try {
      const res = await fetch(`${API_BASE}/expenses/${poEdit.id}/set-po`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ poNumber: (poEdit.draft || '').trim() }),
      });
      const out = await res.json().catch(() => ({}));
      if (!res.ok) { setPoEditError(out.error || 'Could not update the PO.'); return; }
      await refetchLedger();
      setHighlightId(poEdit.id);
      setPoEdit(null);
    } catch (_) {
      setPoEditError('Could not update the PO.');
    } finally {
      setSavingPo(false);
    }
  };

  // Drag-drop receipts straight onto the Ledger → project-agnostic upload
  // (routes by PO; anything unmatched lands in the unallocated queue).
  const handleLedgerUpload = async (files) => {
    const valid = Array.from(files || []).filter(f => f.type.startsWith('image/') || f.type === 'application/pdf');
    if (!valid.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      valid.forEach(f => fd.append('file', f));
      await fetch(`${API_BASE}/expenses/extract-receipt`, { method: 'POST', headers: authHeader(), body: fd });
      setTimeout(() => { fetchUnallocated(); refetchLedger(); }, 4000);
    } catch (_) {
      alert('Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  // Allocate one unallocated item to a project (by PO) or an overhead category.
  // Overhead is picked by ONE choice (the cost code / bucket). The required
  // expense-TYPE `category` is inferred from that bucket's name so the user never
  // labels a receipt twice — "004 · Tools & Equipment" books as Tools, "Gas" as
  // Fuel, etc. Falls back to 'Other' when nothing matches (safe for the enum).
  // Expense TYPES the user can pick when the auto-guess is wrong (e.g. a bucket
  // named "Tools & Equipment" that actually holds a purchased piece of equipment).
  const EXPENSE_TYPES = ['Materials', 'Tools', 'Equipment', 'Equipment Rental',
    'Subcontractor', 'Labor', 'Fuel', 'Permit', 'Travel', 'Other'];
  const deriveCategory = (costCat) => {
    const n = `${costCat?.code || ''} ${costCat?.name || ''}`.toLowerCase();
    if (/\brent/.test(n) && /equip/.test(n)) return 'Equipment Rental';
    if (/tool|equip/.test(n)) return 'Tools';
    if (/fuel|gas\b|gasoline|diesel/.test(n)) return 'Fuel';
    if (/permit/.test(n)) return 'Permit';
    if (/travel|mileage|lodging|hotel|airfare/.test(n)) return 'Travel';
    if (/sub-?contract/.test(n)) return 'Subcontractor';
    if (/labor|payroll|wages/.test(n)) return 'Labor';
    if (/material|suppl/.test(n)) return 'Materials';
    return 'Other';
  };

  const allocateItem = async (item) => {
    const po = (allocPO[item.id] || '').trim();
    const catId = allocCat[item.id] || '';
    const typeOverride = allocType[item.id] || '';  // '' = auto-derive from the bucket
    if (!po && !catId) { alert('Enter a PO number or pick a cost category to allocate this receipt.'); return; }

    const leftovers = unallocated
      .filter(u => String(u.pendingId) === String(item.pendingId) && u.id !== item.id)
      .map(u => { const c = { ...u }; delete c.pendingId; delete c.originalFileName; return c; });
    const base = { ...item };
    delete base.pendingId; delete base.originalFileName;

    const book = (save) => fetch(`${API_BASE}/expenses/batch`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ expenses: [save], pendingId: item.pendingId, leftovers }),
    }).then(r => r.json());
    const finish = async () => {
      setAllocPO(prev => { const c = { ...prev }; delete c[item.id]; return c; });
      setAllocType(prev => { const c = { ...prev }; delete c[item.id]; return c; });
      await fetchUnallocated();
      await refetchLedger();
    };

    try {
      if (po) {
        // Try the number as a PROJECT PO first.
        let out = await book({ ...base, poNumber: po });
        if (out.booked > 0) { await finish(); return; }
        // No project matched — if that number is actually one of your cost-category
        // CODES (e.g. 004 = Tools & Equipment), book it as overhead instead of failing.
        const codeCat = costCategories.find(c => String(c.code || '').trim() === po);
        if (codeCat) {
          out = await book({ ...base, costCategory: codeCat._id, category: typeOverride || deriveCategory(codeCat) });
          if (out.booked > 0) { await finish(); return; }
        }
        alert(codeCat
          ? `Could not allocate to ${codeCat.code} · ${codeCat.name}.`
          : `"${po}" matches no project, and nothing is coded "${po}" in your cost categories. Pick an Overhead category, or check the number.`);
        return;
      }
      // Overhead category chosen from the dropdown.
      const chosenCat = costCategories.find(c => String(c._id) === String(catId));
      const out = await book({ ...base, costCategory: catId, category: typeOverride || deriveCategory(chosenCat) });
      if (out.booked === 0) { alert('Could not allocate to that category.'); return; }
      await finish();
    } catch (_) {
      alert('Could not allocate.');
    }
  };

  // Open the original scanned file for an unallocated receipt. Same temp-file
  // endpoint the scan-review screen uses; iOS-safe (tab opened synchronously).
  const viewUnallocatedReceipt = async (item) => {
    if (!item || !item.pendingId || !item.id) return;
    const win = window.open('', '_blank');
    try {
      const res = await fetch(`${API_BASE}/expenses/pending/${item.pendingId}/receipt/${item.id}`, {
        headers: authHeader(),
      });
      if (!res.ok) throw new Error('fetch failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (win) { win.location = url; } else { window.location.href = url; }
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch (err) {
      if (win) win.close();
      console.error('Open unallocated receipt failed:', err);
      alert('Could not open the original receipt file.');
    }
  };

  // Delete an unallocated receipt (testing/cleanup).
  const deleteUnallocated = async (item) => {
    if (!window.confirm('Delete this unallocated receipt?')) return;
    try {
      await fetch(`${API_BASE}/expenses/unallocated/${item.pendingId}/${item.id}`, {
        method: 'DELETE', headers: authHeader(),
      });
      setUnallocated(prev => prev.filter(u => u.id !== item.id));
      // Emptying a batch resolves its alert — refresh the bell immediately.
      window.dispatchEvent(new Event('notifications:refresh'));
    } catch (_) {
      alert('Could not delete.');
    }
  };

  // Delete a booked ledger expense (e.g. an accidental duplicate scan).
  const deleteExpense = async (exp) => {
    const label = `${exp.isReturn ? 'return from ' : ''}${exp.store || 'this expense'} — ${(Number(exp.totalAmount) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`;
    if (!window.confirm(`Delete ${label}? This can't be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/expenses/${exp._id}`, { method: 'DELETE', headers: authHeader() });
      if (!res.ok) throw new Error('delete failed');
      setData(prev => prev.filter(d => d._id !== exp._id));
    } catch (_) {
      alert('Could not delete this expense.');
    }
  };

  // ---- Swipe-to-delete (mobile card list): swipe left to reveal Delete ----
  // Reveal-then-tap only (no full-swipe shortcut). One card open at a time.
  const [swipedKey, setSwipedKey] = useState(null);
  const [swipeState, setSwipeState] = useState({ key: null, dx: 0, active: false });
  const swipeAteClickAt = useRef(0); // eats the trailing click after a swipe
  const trackSwipe = (key, e) => {
    if (e.pointerType === 'mouse') return; // touch/pen only
    const sx = e.clientX, sy = e.clientY;
    let decided = null, lastDx = 0;
    const off = () => {
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
    const mv = (ev) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (!decided) {
        if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
        decided = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
        if (decided === 'v') { off(); return; } // scrolling — leave it alone
      }
      lastDx = Math.max(-100, Math.min(20, dx));
      setSwipeState({ key, dx: Math.min(0, lastDx), active: true });
    };
    const up = () => {
      off();
      setSwipeState({ key: null, dx: 0, active: false });
      if (decided === 'h') {
        swipeAteClickAt.current = Date.now();
        if (lastDx < -45) setSwipedKey(key);           // opened
        else setSwipedKey(k => (k === key ? null : k)); // closed / not enough
      }
    };
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
  };
  // Any tap elsewhere closes an open swipe.
  useEffect(() => {
    if (!swipedKey) return;
    const close = () => setSwipedKey(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [swipedKey]);

  const SwipeWrap = (key, onDelete, children, rounded = '') => {
    const open = swipedKey === key;
    const liveDx = swipeState.key === key ? swipeState.dx : 0;
    const dx = open ? Math.min(-72, liveDx || -72) : Math.min(0, liveDx);
    return (
      <div className={`relative overflow-hidden ${rounded}`}>
        {(open || dx < 0) && (
          <button
            onClick={(e) => { e.stopPropagation(); setSwipedKey(null); onDelete(e); }}
            className="absolute inset-y-0 right-0 w-20 bg-red-600 active:bg-red-500 text-white text-xs font-black uppercase flex items-center justify-center"
          >
            Delete
          </button>
        )}
        <div
          style={{ transform: `translateX(${dx}px)`, transition: swipeState.key === key && swipeState.active ? 'none' : 'transform 0.22s ease' }}
          className="relative"
        >
          {children}
        </div>
      </div>
    );
  };

  // Close the orphan view when clicking outside it (but not on its toggle button).
  useEffect(() => {
    if (!showOrphans) return;
    const onDoc = (e) => {
      if (orphanRef.current && !orphanRef.current.contains(e.target) &&
          orphanBtnRef.current && !orphanBtnRef.current.contains(e.target)) {
        setShowOrphans(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [showOrphans]);

  // ---- Filters (narrow the global feed; sorting still handled by the table) ----
  const [search, setSearch] = useState('');
  const [methodFilter, setMethodFilter] = useState('All');
  const [projectFilter, setProjectFilter] = useState('All');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // "To" follows "From" until set by hand, so backdated ranges don't open
  // the picker on the current month. Cleared = follows again.
  const [endDateTouched, setEndDateTouched] = useState(false);

  const projectOptions = useMemo(() => {
    const set = new Set(data.map(e => e.project?.poNumber).filter(Boolean));
    return Array.from(set).sort();
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter(e => {
      if (methodFilter !== 'All' && (e.paymentMethod || 'Card') !== methodFilter) return false;
      if (projectFilter !== 'All') {
        if (projectFilter.startsWith('cat:')) {
          if (String(e.costCategory?._id || '') !== projectFilter.slice(4)) return false;
        } else if ((e.project?.poNumber || '') !== projectFilter) {
          return false;
        }
      }
      if (startDate && new Date(e.date) < new Date(startDate)) return false;
      if (endDate && new Date(e.date) > new Date(endDate + 'T23:59:59')) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [
          e.store, e.storeLocation, e.receiptNumber, e.project?.poNumber, e.project?.title,
          e.enteredBy?.name, e.uploadedBy?.name, e.category, e.notes, e.paymentMethod
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [data, search, methodFilter, projectFilter, startDate, endDate]);

  // Arriving back from a project's expense row: reveal the row (clear any active
  // filters so it's guaranteed visible), scroll to it, and pulse it for ~1.3s.
  // A ref guard ensures we run once per id — we deliberately do NOT mutate the
  // navigation state to "consume" it, because that re-render would tear down the
  // clear-timer and leave the row highlighted forever.
  const handledHighlightRef = useRef(null);
  useEffect(() => {
    const hid = location.state?.highlightId;
    if (!hid || loading || data.length === 0) return;
    if (handledHighlightRef.current === hid) return;
    handledHighlightRef.current = hid;
    setSearch(''); setMethodFilter('All'); setProjectFilter('All'); setStartDate(''); setEndDate('');
    setHighlightId(hid);
    const tScroll = setTimeout(() => {
      const el = document.getElementById(`ledger-row-${hid}`);
      if (!el) return;
      // Only scroll if the row isn't already fully in view.
      const rect = el.getBoundingClientRect();
      const inView = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
      if (!inView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
    const tClear = setTimeout(() => setHighlightId(null), 800);
    return () => { clearTimeout(tScroll); clearTimeout(tClear); };
  }, [location.state, loading, data.length]);

  useEffect(() => {
    const fetchExpenses = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE}/expenses`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          console.log("🔥 DATABASE RETURNED THIS EXACT DATA:", result);
          setData(result);
        } else {
          console.error("🔥 DATABASE REJECTED FETCH. Status:", response.status);
        }
      } catch (error) {
        console.error('Failed to fetch expenses:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchExpenses();
  }, []);

// Open Receipt in New Tab (Forcing View instead of Download)
  const handleViewReceipt = async (expense) => {
    if (!expense.receiptImagePath) return;

    // iOS Safari blocks window.open() called after an await, so open the tab
    // synchronously on tap and point it at the blob once it's ready.
    const win = window.open('', '_blank');
    try {
      setOpeningReceiptId(expense._id);
      const token = localStorage.getItem('token');
      
      // 1. Get the raw Dropbox link
      const response = await fetch(`${API_BASE}/projects/files/link?path=${encodeURIComponent(expense.receiptImagePath)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        // 2. INTERCEPT: Fetch the file into browser memory instead of downloading
        const fileResponse = await fetch(data.link);
        const rawBlob = await fileResponse.blob();
        
        // 3. Force the correct format based on the file extension
        const isPdf = expense.receiptImagePath.toLowerCase().endsWith('.pdf');
        const fileType = isPdf ? 'application/pdf' : 'image/jpeg';
        const finalBlob = new Blob([rawBlob], { type: fileType });
        
        // 4. Create a temporary secure viewer link and open it
        const fileUrl = URL.createObjectURL(finalBlob);
        if (win) { win.location = fileUrl; } else { window.location.href = fileUrl; }

        // 5. Clean up the memory after 10 seconds to prevent leaks
        setTimeout(() => URL.revokeObjectURL(fileUrl), 10000);
      } else {
        if (win) win.close();
        console.error("Failed to fetch receipt link");
      }
    } catch (error) {
      if (win) win.close();
      console.error('Error opening receipt:', error);
    } finally {
      setOpeningReceiptId(null);
    }
  };

  // Date-only values ("2026-09-02" / ISO midnight) must be read as a CALENDAR
  // date, not a UTC instant — new Date() shifts them a day early in Pacific.
  const fmtLedgerDate = (v) => {
    if (!v) return '—';
    const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString();
    return new Date(v).toLocaleDateString();
  };

  // 2. COLUMN DEFINITIONS (Locked & Mapped to Backend)
  const columns = useMemo(() => [
    {
      accessorKey: 'date',
      header: 'Date',
      cell: info => fmtLedgerDate(info.getValue()),
      size: 100,
    },
    {
      accessorKey: 'store',
      header: 'Vendor',
      cell: info => {
        const r = info.row.original;
        const loc = r.storeLocation;
        const review = r.needsReview;
        const note = r.mathNote;
        return (
          <div className="truncate w-full pr-2" title={info.getValue()}>
            <span className="flex items-center gap-1">
              {review && <span title={r.reviewReason || 'Needs review'}>⚠️</span>}
              {!review && note && <span className="text-zinc-500" title={note}>ⓘ</span>}
              {info.getValue()}
            </span>
            {loc && <span className="block text-[10px] text-zinc-500 truncate">{loc}</span>}
          </div>
        );
      },
      size: 150,
    },
    {
      id: 'poNumber',
      header: 'Project/PO',
      // 🚀 Tell the table EXACTLY what string to sort by
      accessorFn: row => row.project?.poNumber || '',
      cell: info => {
        const proj = info.row.original.project;
        const expId = info.row.original._id;
        const po = info.getValue();
        // No project -> overhead (show its cost code) or truly unallocated.
        if (!proj?._id) {
          const cc = info.row.original.costCategory;
          if (cc?.code || cc?.name) {
            return (
              <span
                title={cc.name || ''}
                className="bg-amber-500/10 text-amber-400 px-2 py-1 rounded text-xs font-bold truncate"
              >
                #{cc.code || cc.name}
              </span>
            );
          }
          return (
            <span className="bg-zinc-500/10 text-zinc-500 px-2 py-1 rounded text-xs font-bold truncate">
              Unallocated
            </span>
          );
        }
        // Click jumps to this expense inside its project's Finances tab and
        // carries breadcrumbs so the row highlights there and Back returns here.
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${proj._id}?tab=finances`, {
                state: { fromLedger: true, highlightExpenseId: expId, ledgerHighlightId: expId },
              });
            }}
            title="Open this expense in its project"
            className="bg-indigo-500/10 text-indigo-400 hover:bg-indigo-500/20 hover:text-indigo-300 px-2 py-1 rounded text-xs font-bold truncate underline-offset-2 hover:underline cursor-pointer"
          >
            #{po}
          </button>
        );
      },
      size: 110,
    },
    // Overhead cost category (shown when an expense is booked to overhead
    // instead of a project). Mutually exclusive with Project/PO in practice.
    {
      id: 'costCategory',
      header: 'Category',
      accessorFn: row => row.costCategory?.name || '',
      cell: info => {
        const c = info.row.original.costCategory;
        if (!c) return <span className="text-zinc-600 text-xs">—</span>;
        return (
          <span className="bg-amber-500/10 text-amber-400 px-2 py-1 rounded text-xs font-semibold truncate">
            {c.code ? `${c.code} · ` : ''}{c.name}
          </span>
        );
      },
      size: 130,
    },
    // 🚀 UPLOADED BY (provenance — who brought the file in)
    {
      accessorFn: (row) => row.uploadedBy?.name || row.enteredBy?.name || 'System',
      id: 'uploadedBy',
      header: 'Uploaded By',
      cell: info => (
        <span className="text-zinc-400 font-medium text-xs">
          {info.getValue()}
        </span>
      ),
      size: 130,
    },
    // Payment method (document-driven: Card / Account / ACH ...)
    {
      accessorFn: (row) => row.paymentMethod || 'Card',
      id: 'paymentMethod',
      header: 'Method',
      cell: info => {
        const m = info.getValue();
        // Account invoices (CED, etc.) display as "Credit Account". We do NOT
        // show a paid/unpaid status — the app has no way to know whether an
        // account invoice has actually been paid, so asserting "unpaid" would be
        // misleading. (Card/ACH/etc. show as-is.)
        const label = m === 'Account' ? 'Credit Account' : m;
        const color = m === 'Account' ? 'text-amber-400' : m === 'ACH' ? 'text-sky-400' : 'text-zinc-400';
        return (
          <span className={`text-xs font-medium ${color}`}>{label}</span>
        );
      },
      size: 120,
    },
    {
      accessorKey: 'receiptNumber',
      header: 'Receipt #',
      cell: info => {
        const rowData = info.row.original;
        let linkedReceiptNumber = null;

        // If this receipt has a linkId, scan the table's data array to find its partner
        if (rowData.linkId) {
          const allData = info.table.options.data;
          const linkedReceipt = allData.find(
            item => item.linkId === rowData.linkId && item._id !== rowData._id
          );
          
          if (linkedReceipt) {
            linkedReceiptNumber = linkedReceipt.receiptNumber;
          }
        }

        return (
          <div className="flex flex-col justify-center">
            {rowData.receiptImagePath ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleViewReceipt(rowData); }}
                className="truncate w-full pr-2 text-left text-indigo-300 hover:text-indigo-200 underline underline-offset-2"
                title="Open receipt PDF"
              >
                {rowData.receiptNumber || '—'}
              </button>
            ) : (
              <span className="truncate w-full pr-2 text-zinc-300" title={rowData.receiptNumber}>
                {rowData.receiptNumber || '—'}
              </span>
            )}

            {/* The tiny subscript for linked receipts */}
            {linkedReceiptNumber && (
              <span
                className="text-[10px] text-indigo-400/80 font-medium tracking-wide truncate pr-2 mt-0.5"
                title={`Linked to #${linkedReceiptNumber}`}
              >
                linked to #{linkedReceiptNumber}
              </span>
            )}

            {/* Split-return badge: which slice of a multi-reference return this
                is. Click to flash the other slices of the same slip. */}
            {rowData.splitCount > 1 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const key = rowData.normalizedOriginalReceiptNumber || rowData.originalReceiptNumber;
                  if (!key) return;
                  setHighlightPurchaseKey(key);
                  setTimeout(() => {
                    const target = (info.table.options.data || []).find(
                      x => !x.isReturn && (x.normalizedReceiptNumber === key || x.receiptNumber === key)
                    );
                    if (target) {
                      const el = document.getElementById(`ledger-row-${target._id}`);
                      if (el) {
                        const rect = el.getBoundingClientRect();
                        const inView = rect.top >= 0 && rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
                        if (!inView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }
                    }
                  }, 30);
                  setTimeout(() => setHighlightPurchaseKey(null), 300);
                }}
                title="Click to highlight the original purchase this refunds"
                className="mt-0.5 w-fit text-[10px] font-semibold text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 px-1.5 py-0.5 rounded"
              >
                {(rowData.splitIndex ?? 0) + 1} of {rowData.splitCount} · part of return #{rowData.receiptNumber}
              </button>
            )}
          </div>
        );
      },
      size: 180,
    },
    {
      accessorKey: 'amount', // Using 'amount' as it maps to the subtotal in your DB
      header: 'Subtotal',
      cell: info => `$${(info.getValue() || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
      size: 90,
    },
    {
      accessorKey: 'taxAmount',
      header: 'Tax',
      cell: info => `$${(info.getValue() || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}`,
      size: 80,
    },
    {
      accessorKey: 'totalAmount',
      header: 'Total',
      cell: info => {
        const rowData = info.row.original;
        
        // Check if this specific receipt was flagged as the secondary slip
        const isSecondary = rowData.linkId && rowData.isPrimary === false;
        const val = Number(info.getValue() || 0);
        const isCredit = rowData.isReturn || val < 0; // returns = positive green credit

        return (
          <div className="flex flex-col justify-center">
            <span className={`font-bold ${isSecondary ? 'text-zinc-600 line-through' : isCredit ? 'text-emerald-400' : 'text-white'}`}>
              {isCredit ? '+' : ''}${Math.abs(val).toLocaleString(undefined, {minimumFractionDigits: 2})}
            </span>
            
            {/* Show a tiny orange warning if this amount is skipped in your books */}
            {isSecondary && (
              <span className="text-[9px] text-orange-400 font-medium tracking-wide mt-0.5">
                skipped in total
              </span>
            )}
          </div>
        );
      },
      size: 100,
    }
  ], []);

  // 3. TABLE STATE
  const [openingReceiptId, setOpeningReceiptId] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  // Load saved widths from local storage (or default to empty object if none exist)
  const [columnSizing, setColumnSizing] = useState(() => {
    const savedSizes = localStorage.getItem('expenseLedgerColumnWidths');
    return savedSizes ? JSON.parse(savedSizes) : {};
  });

  // Save widths to local storage every time they change
  useEffect(() => {
    localStorage.setItem('expenseLedgerColumnWidths', JSON.stringify(columnSizing));
  }, [columnSizing]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      columnVisibility,
      columnSizing,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange', 
    enableColumnResizing: true,   
  });

  return (
    <div className="p-6 bg-zinc-950 min-h-screen text-zinc-100">
      {/* Header */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-black tracking-tight">Expense Ledger</h1>
          <p className="text-zinc-500 text-sm mt-1">Global audit log for Stalwart Electric projects</p>
        </div>

        <div className="flex items-center gap-3">
        {/* Global receipt upload — same scanner as mobile. Approve directly or
            hand off to PMs. Admins / super-admins only. */}
        {canScan && (
          <button
            onClick={() => setShowScanner(true)}
            className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg shadow-indigo-900/30"
          >
            <Upload size={16} /> Add Receipt
          </button>
        )}

        {/* Unallocated queue toggle (replaces the old orphan project) */}
        <button
          ref={orphanBtnRef}
          onClick={() => setShowOrphans(o => !o)}
          className={`relative flex items-center gap-2 border px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
            showOrphans ? 'bg-amber-600/20 border-amber-500/40 text-amber-300' : 'bg-zinc-900 border-zinc-800 hover:bg-zinc-800'
          }`}
        >
          <Inbox size={16} />
          Pending Review
          {/* Orange = unallocated receipts; Purple = awaiting PM review. Show
              each only when it has a count, so both can appear side by side. */}
          {unallocated.length > 0 && (
            <span
              title="Unallocated receipts"
              className="min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-zinc-950 text-[10px] font-bold flex items-center justify-center"
            >
              {unallocated.length > 99 ? '99+' : unallocated.length}
            </span>
          )}
          {isSuperadmin && pendingReviewTotal > 0 && (
            <span
              title="Awaiting PM review"
              className="min-w-[18px] h-[18px] px-1 rounded-full bg-purple-500 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {pendingReviewTotal > 99 ? '99+' : pendingReviewTotal}
            </span>
          )}
        </button>

        {/* Column Settings */}
        <div className="relative group">
          <button className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-zinc-800 transition-all">
            <Settings2 size={16} />
            Manage Columns
          </button>

          <div className="absolute right-0 mt-2 w-56 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl hidden group-hover:block z-50 p-3 ring-1 ring-white/5">
            <p className="text-[10px] uppercase font-bold text-zinc-500 mb-2 px-2">Show/Hide Columns</p>
            {table.getAllLeafColumns().map(column => (
              <label key={column.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-800 rounded-lg cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={column.getIsVisible()}
                  onChange={column.getToggleVisibilityHandler()}
                  className="w-4 h-4 rounded border-zinc-700 bg-zinc-800 text-indigo-600 focus:ring-0 focus:ring-offset-0"
                />
                <span className="text-sm font-medium text-zinc-300 capitalize">
                  {column.columnDef.header || column.id.replace(/([A-Z])/g, ' $1')}
                </span>
              </label>
            ))}
          </div>
        </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search vendor, receipt #, project, uploader…"
          className="flex-1 min-w-[240px] bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-2.5 text-sm text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-indigo-600/60"
        />
        <select
          value={projectFilter}
          onChange={e => setProjectFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60"
        >
          <option value="All">All allocations</option>
          <optgroup label="Projects">
            {projectOptions.map(po => <option key={po} value={po}>#{po}</option>)}
          </optgroup>
          {costCategories.length > 0 && (
            <optgroup label="Overhead">
              {costCategories.map(c => (
                <option key={c._id} value={`cat:${c._id}`}>{c.code} · {c.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        <select
          value={methodFilter}
          onChange={e => setMethodFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60"
        >
          <option value="All">All methods</option>
          <option value="Card">Card</option>
          <option value="Account">Credit Account</option>
          <option value="ACH">ACH</option>
          <option value="Cash">Cash</option>
          <option value="Check">Check</option>
        </select>
        <input
          type="date" value={startDate}
          onChange={e => {
            const start = e.target.value;
            setStartDate(start);
            // "To" follows "From" unless set by hand — and never before it.
            if (!endDateTouched || (endDate && start > endDate)) setEndDate(start);
          }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60"
          title="From date"
        />
        <input
          type="date" value={endDate} min={startDate || undefined}
          onChange={e => {
            const end = e.target.value;
            setEndDateTouched(!!end);
            setEndDate(end);
          }}
          className="bg-zinc-900 border border-zinc-800 rounded-xl px-3 py-2.5 text-sm text-zinc-200 focus:outline-none focus:border-indigo-600/60"
          title="To date"
        />
        {(search || methodFilter !== 'All' || projectFilter !== 'All' || startDate || endDate) && (
          <button
            onClick={() => { setSearch(''); setMethodFilter('All'); setProjectFilter('All'); setStartDate(''); setEndDate(''); setEndDateTouched(false); }}
            className="text-xs text-zinc-400 hover:text-zinc-200 px-3 py-2.5"
          >
            Clear
          </button>
        )}
        <span className="text-xs text-zinc-500 ml-auto">{filteredData.length} of {data.length}</span>
      </div>

      {/* Table area — drag receipts here to scan (project-agnostic upload) */}
      <div
        className="relative"
        onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); if (!ledgerDragging) setLedgerDragging(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setLedgerDragging(false); }}
        onDrop={(e) => { e.preventDefault(); setLedgerDragging(false); handleLedgerUpload(e.dataTransfer.files); }}
      >
        {ledgerDragging && (
          <div className="absolute inset-0 z-30 rounded-2xl border-2 border-dashed border-indigo-500 bg-indigo-500/10 pointer-events-none" />
        )}
        {uploading && <div className="mb-3 text-xs text-indigo-300">Uploading & scanning…</div>}

        {showOrphans ? (
          <div ref={orphanRef} className="space-y-4">
          {/* ============ UNALLOCATED REVIEW TABLE ============ */}
          <div className="border border-amber-600/30 rounded-2xl bg-zinc-900/40 overflow-hidden">
            <div className="px-5 py-3 border-b border-amber-600/20 bg-amber-600/10 flex items-center justify-between">
              <span className="text-sm font-semibold text-amber-200">Unallocated receipts — assign a PO to file each one</span>
              <span className="text-xs text-amber-300/70">{unallocated.length} waiting</span>
            </div>
            {unallocated.length === 0 ? (
              <div className="px-5 py-10 text-center text-zinc-500 text-sm">Nothing unallocated. You're all caught up.</div>
            ) : (
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-[11px] uppercase tracking-wider text-zinc-500">
                    <th className="px-4 py-3">Vendor</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Receipt #</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Why</th>
                    <th className="px-4 py-3 w-56">Allocate to PO</th>
                    <th className="px-4 py-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {unallocated.map(item => {
                    const val = Number(item.totalAmount || 0);
                    const credit = item.isReturn || val < 0;
                    return (
                      <tr
                        key={item.id}
                        onClick={() => viewUnallocatedReceipt(item)}
                        title="Click to view the original receipt"
                        className="border-b border-zinc-800/50 hover:bg-zinc-800/20 cursor-pointer"
                      >
                        <td className="px-4 py-3">
                          <div className="text-zinc-200">{item.store}</div>
                          {item.storeLocation && <div className="text-[10px] text-zinc-500">{item.storeLocation}</div>}
                        </td>
                        <td className="px-4 py-3 text-zinc-400">{(item.date || '').split('T')[0]}</td>
                        <td className="px-4 py-3 font-mono text-zinc-400">
                          {item.isReturn
                            ? <span className="text-orange-300">↩ {item.originalReceiptNumber || '(unknown)'}</span>
                            : (item.receiptNumber || '—')}
                        </td>
                        <td className={`px-4 py-3 text-right font-bold ${credit ? 'text-emerald-400' : 'text-zinc-200'}`}>
                          {credit ? '+' : ''}${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-[11px] text-amber-300/80 max-w-[200px] truncate" title={item.reviewReason}>{item.reviewReason}</td>
                        {/* Allocation controls: clicks here must NOT open the receipt */}
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-2">
                            <input
                              value={allocPO[item.id] || ''}
                              onChange={(e) => setAllocPO(prev => ({ ...prev, [item.id]: e.target.value }))}
                              placeholder="PO #"
                              className="w-16 bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-sm text-white"
                            />
                            <span className="text-[10px] text-zinc-600">or</span>
                            <select
                              value={allocCat[item.id] || ''}
                              onChange={(e) => setAllocCat(prev => ({ ...prev, [item.id]: e.target.value }))}
                              className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-white max-w-[130px]"
                            >
                              <option value="">Overhead…</option>
                              {costCategories.map(c => <option key={c._id} value={c._id}>{c.code} · {c.name}</option>)}
                            </select>
                            <select
                              value={allocType[item.id] || ''}
                              onChange={(e) => setAllocType(prev => ({ ...prev, [item.id]: e.target.value }))}
                              title="Expense type — leave on Auto to guess from the bucket"
                              className="bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-xs text-zinc-300 max-w-[120px]"
                            >
                              <option value="">Type: auto</option>
                              {EXPENSE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                            <button
                              onClick={() => allocateItem(item)}
                              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-medium"
                            >
                              Allocate
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => deleteUnallocated(item)}
                            className="text-zinc-600 hover:text-red-400 transition-colors"
                            title="Delete this unallocated receipt"
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ============ AWAITING PM REVIEW (per-project, Super Admin) ============ */}
          {isSuperadmin && (
            <div className="border border-indigo-600/30 rounded-2xl bg-zinc-900/40 overflow-hidden">
              <div className="px-5 py-3 border-b border-indigo-600/20 bg-indigo-600/10 flex items-center justify-between">
                <span className="text-sm font-semibold text-indigo-200">Awaiting PM review — by project</span>
                <span className="text-xs text-indigo-300/70">{pendingReviews.length} project{pendingReviews.length === 1 ? '' : 's'}</span>
              </div>
              {pendingReviews.length === 0 ? (
                <div className="px-5 py-10 text-center text-zinc-500 text-sm">No projects waiting on review. Everyone's caught up.</div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-[11px] uppercase tracking-wider text-zinc-500">
                      <th className="px-4 py-3">Project</th>
                      <th className="px-4 py-3">Manager</th>
                      <th className="px-4 py-3 text-center">Receipts</th>
                      <th className="px-4 py-3">Pending</th>
                      <th className="px-4 py-3 w-44"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingReviews.map(row => {
                      const ageLabel = row.ageDays <= 0 ? 'Today' : `${row.ageDays} day${row.ageDays === 1 ? '' : 's'}`;
                      const goReview = () => navigate(`/projects/${row.projectId}?tab=finances`, { state: { goToReview: true } });
                      return (
                        <tr key={row.projectId} className="border-b border-zinc-800/50 hover:bg-zinc-800/20">
                          <td className="px-4 py-3">
                            <button onClick={goReview} className="text-indigo-300 hover:text-indigo-200 font-bold underline-offset-2 hover:underline">
                              #{row.poNumber}
                            </button>
                            {row.title && <div className="text-[10px] text-zinc-500 truncate max-w-[180px]">{row.title}</div>}
                          </td>
                          <td className="px-4 py-3 text-zinc-300">{row.pmName}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="min-w-[20px] inline-flex items-center justify-center px-1.5 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold">
                              {row.count}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-zinc-400">{ageLabel}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 justify-end">
                              <button onClick={goReview} className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 rounded-lg text-xs font-medium">Jump</button>
                              <button
                                onClick={() => nudgePM(row)}
                                disabled={row.pmName === 'Unassigned' || nudgedId === row.projectId}
                                className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white rounded-lg text-xs font-medium"
                                title={row.pmName === 'Unassigned' ? 'No PM assigned to nudge' : 'Send this PM a reminder'}
                              >
                                {nudgedId === row.projectId ? 'Nudged ✓' : 'Nudge PM'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
          </div>
        ) : (
      <>
      {/* ===== MOBILE CARD LIST (phones only — desktop table below untouched).
          Uses the same row model, so search/filter/sort all still apply. ===== */}
      <div className="md:hidden space-y-3 pb-4">
        {loading ? (
          <p className="text-center text-zinc-500 pt-10">Loading expenses...</p>
        ) : table.getRowModel().rows.length === 0 ? (
          <p className="text-center text-zinc-500 pt-10">No expenses found.</p>
        ) : (
          table.getRowModel().rows.map(row => {
            const e = row.original;
            return (
              <React.Fragment key={row.id}>
              {SwipeWrap(`exp:${e._id}`, () => deleteExpense(e),
              <div
                onPointerDown={(ev) => trackSwipe(`exp:${e._id}`, ev)}
                onClick={() => { if (Date.now() - swipeAteClickAt.current < 350) return; handleViewReceipt(e); }}
                style={{ touchAction: 'pan-y' }}
                className={`bg-zinc-900/60 border rounded-2xl p-4 transition-colors ${
                  e.needsReview ? 'border-amber-500/60' : 'border-zinc-800'
                } ${e.receiptImagePath ? 'active:bg-zinc-800/70' : 'opacity-70'}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-zinc-100 font-semibold min-w-0 break-words">
                    {e.isReturn ? '↩︎ ' : ''}{e.store || 'Unknown vendor'}
                  </p>
                  <p className={`font-bold shrink-0 ${e.isReturn ? 'text-emerald-400' : 'text-zinc-100'}`}>
                    {(Number(e.totalAmount) || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                  </p>
                </div>
                <p className="text-zinc-500 text-xs mt-1">
                  {fmtLedgerDate(e.date)}
                  {e.project?.poNumber ? ` · PO #${e.project.poNumber}` : ' · Unallocated'}
                  {e.costCategory?.name ? ` · ${e.costCategory.name}` : ''}
                </p>
                <p className="text-zinc-600 text-xs mt-0.5">
                  {e.needsReview ? '⚠️ Needs review · ' : ''}
                  {e.receiptImagePath ? 'Tap to view receipt' : 'No receipt image'}
                </p>
              </div>
              , 'rounded-2xl')}
              </React.Fragment>
            );
          })
        )}
      </div>

      <div className="max-md:hidden border border-zinc-800 rounded-2xl bg-zinc-900/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table
            style={{ width: table.getTotalSize() }}
            className="w-full text-left border-collapse table-fixed"
          >
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id} className="bg-zinc-900/80 border-b border-zinc-800">
                  {headerGroup.headers.map(header => (
                    <th
                      key={header.id}
                      style={{ width: header.getSize(), position: 'relative' }}
                      className="px-4 py-4 text-[11px] font-bold uppercase tracking-widest text-zinc-500 group"
                    >
                      <div 
                        className={`flex items-center gap-2 truncate pr-4 ${header.column.getCanSort() ? 'cursor-pointer select-none' : ''}`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        
                        {/* 🚀 Dynamic Icon based on sort direction */}
                        {{
                          asc: <ArrowUp size={12} className="text-indigo-400 flex-shrink-0" />,
                          desc: <ArrowDown size={12} className="text-indigo-400 flex-shrink-0" />,
                        }[header.column.getIsSorted()] ?? (
                          <ArrowUpDown size={12} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        )}
                      </div>

                      {/* 🚀 RESTORED DRAG-TO-RESIZE HANDLE */}
                      <div
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={`absolute right-0 top-0 h-full w-1.5 cursor-col-resize transition-all hover:bg-indigo-500 ${
                          header.column.getIsResizing() ? 'bg-indigo-500 w-1.5' : 'bg-transparent'
                        }`}
                      />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-zinc-500">
                    Loading expenses...
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-zinc-500">
                    No expenses found.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr
                    key={row.id}
                    id={`ledger-row-${row.original._id}`}
                    onDoubleClick={() => setPoEdit({
                      id: row.original._id,
                      store: row.original.store,
                      current: row.original.project?.poNumber || '',
                      draft: row.original.project?.poNumber || '',
                    })}
                    title="Double-click to edit the PO #"
                    className={`border-b border-zinc-800/50 transition-all group relative hover:bg-zinc-800/20
                      ${row.original.receiptImagePath ? '' : 'opacity-70'}
                      ${row.original.needsReview ? 'border-l-2 border-l-amber-500' : ''}
                      ${highlightId === row.original._id ? 'bg-indigo-500/20 ring-2 ring-inset ring-indigo-400' : ''}
                      ${highlightPurchaseKey && !row.original.isReturn && (row.original.normalizedReceiptNumber === highlightPurchaseKey || row.original.receiptNumber === highlightPurchaseKey) ? 'bg-emerald-500/15 ring-2 ring-inset ring-emerald-400' : ''}
                    `}
                  >{/* Loading Overlay for this specific row */}
                    {openingReceiptId === row.original._id && (
                      <td colSpan={columns.length} className="absolute inset-0 bg-indigo-500/10 backdrop-blur-[1px] flex items-center justify-center z-10 border-l-2 border-indigo-500">
                        <div className="flex items-center gap-2 text-indigo-400 font-medium text-sm drop-shadow-md">
                          <div className="w-4 h-4 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin" />
                          Opening File...
                        </div>
                      </td>
                    )}
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        style={{ width: cell.column.getSize(), maxWidth: cell.column.getSize() }}
                        className="px-4 py-4 text-sm text-zinc-300 truncate"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
        )}
      </div>

      {showScanner && (
        <GlobalReceiptScanner
          receiptsOnly
          initialPendingId={localStorage.getItem('adminScanPendingId') || null}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* Double-click-a-row PO editor */}
      {poEdit && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => !savingPo && setPoEdit(null)}
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-2xl p-6 w-full max-w-sm shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-white mb-1">Edit PO #</h3>
            <p className="text-xs text-zinc-500 mb-4">
              {poEdit.store ? `${poEdit.store} · ` : ''}Enter a project PO, or a cost code (e.g. 004) for overhead. Leave blank to move it to Unallocated.
            </p>
            <input
              autoFocus
              value={poEdit.draft}
              onChange={(e) => { setPoEdit(p => ({ ...p, draft: e.target.value })); setPoEditError(''); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') savePoEdit();
                if (e.key === 'Escape' && !savingPo) setPoEdit(null);
              }}
              placeholder="PO #"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-white font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            {poEditError && <p className="text-xs text-red-400 mt-2">{poEditError}</p>}
            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setPoEdit(null)}
                disabled={savingPo}
                className="px-4 py-2 text-sm text-zinc-400 hover:text-white rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={savePoEdit}
                disabled={savingPo || poEdit.draft.trim() === poEdit.current.trim()}
                className="px-4 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingPo ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseLedger;